// src/components/Emo.jsx
// 3.42.2: icons that move. Each emoji gets a motion that fits what it does
// (Compose writes, Outbox sends, Inbox drops in, the Console gear turns…).
// It plays while you point at the menu item, tab, card or button it's in,
// and keeps going on the page you're on (aria-current / aria-selected /
// aria-pressed). The motions are in App.jsx (.cp-ico, .ico-*); nothing moves
// for people who ask their computer for less motion.
//   <Emo e="📤" />          one icon
//   emoLabel('📤 Outbox')   a label that starts with an emoji → icon + text

import { FLUX_SVG, EMOJI_TO_FLUX, FLUX_CSS } from './fluxIcons';
const MOTION = {
  '🏠': 'hop', '✍️': 'write', '✍': 'write', '📤': 'send', '📥': 'drop', '🗑️': 'shake', '🗑': 'shake', '📇': 'flip',
  '🧾': 'scroll', '📈': 'grow', '📊': 'grow', '👥': 'nod', '👤': 'nod', '📠': 'print', '⚙️': 'spin', '⚙': 'spin', '📚': 'tilt',
  '🕒': 'spin', '⏳': 'flipv', '🩺': 'beat', '🔁': 'spin', '↻': 'spin', '💰': 'coin', '🔒': 'shake', '🔐': 'shake', '🗄️': 'drawer',
  '🤖': 'bob', '📝': 'write', '🛟': 'spin', '💬': 'bob', '✨': 'twinkle', '❓': 'bob', '🔍': 'search', '🔎': 'search', '🖥️': 'bob',
  '🏢': 'hop', '💼': 'hop', '✉️': 'send', '📧': 'send', '📨': 'send', '🚫': 'shake', '🚷': 'shake', '🛡️': 'beat', '💳': 'flip', '📞': 'ring',
  '📡': 'beat', '☁️': 'float', '🎓': 'hop', '🎬': 'shake', '🗂️': 'flip', '📋': 'tilt', '🧭': 'spin', '🔔': 'ring', '👁️': 'blink', '👁': 'blink',
  '🧮': 'abacus', '📄': 'flip', '🔑': 'tilt',
  // 3.43.5: the owner's picks for Settings, Logs, Users, Usage, Articles
  '🛠️': 'shake', '🛠': 'shake', '📁': 'drawer', '🎭': 'tilt', '👨🏻‍💻': 'type', '👨‍💻': 'type', '📖': 'flip', '🖼️': 'tilt', '🕘': 'spin', '📵': 'shake', '🧩': 'tilt', '⚠️': 'shake', '⚠': 'shake',
  '⭐': 'twinkle', '★': 'twinkle', '📅': 'flip', '🗓️': 'flip', '📜': 'scroll', '🚀': 'send', '➕': 'spin', '+': 'spin', '⬇': 'drop', '📎': 'tilt',
};

export const motionOf = e => MOTION[e] || MOTION[String(e || '').replace(/\uFE0F/g, '')] || 'pop';

// 3.55.0 (owner, Oct 11): the portal's own icons, from the owner's Flux Icons kit (Claude
// Design; components/fluxIcons.js) instead of emoji: colour icons, the same on every device,
// each with its own motion, which plays where the old motion did (FLUX_CSS). Emoji without
// an icon (profile-picture choices, marks inside text such as ✓ ✕ ★) stay as they are.
let fluxCssAdded = false;
function addFluxCss() {
  if (fluxCssAdded || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.id = 'flux-icons';
  el.textContent = FLUX_CSS;
  document.head.appendChild(el);
  fluxCssAdded = true;
}
export const fluxNameOf = e => EMOJI_TO_FLUX[String(e || '').replace(/\uFE0F/g, '')] || null;

// 3.55.0: the same icons inside HTML text the portal shows as is (Library articles):
// each icon emoji in the text (never inside a tag) becomes the Flux icon; the marks we
// keep as text (✓ ✕ ★ ← → ↩) stay
const KEEP_AS_TEXT = new Set(['✕', '★', '↩']);
let EMOJI_RE = null;
export function fluxHtml(html) {
  if (!html) return html;
  if (!EMOJI_RE) {
    const keys = Object.keys(EMOJI_TO_FLUX).filter(k => !KEEP_AS_TEXT.has(k)).sort((x, y) => y.length - x.length);
    EMOJI_RE = new RegExp(keys.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + '\uFE0F?', 'gu');
  }
  addFluxCss();
  return String(html).split(/(<[^>]*>)/).map(part => (part.startsWith('<') ? part : part.replace(EMOJI_RE, m => {
    const name = fluxNameOf(m);
    return name ? `<span class="cp-ico cp-ico-flux" aria-hidden="true" style="display:inline-block;width:1.25em;height:1.25em;vertical-align:-0.27em">${FLUX_SVG[name]}</span>` : m;
  }))).join('');
}

export default function Emo({ e, className = '', style }) {
  if (!e) return null;
  const name = fluxNameOf(e);
  if (name) {
    addFluxCss();
    // our own drawings from our own bundle (no outside content), so inlining them is safe
    return <span className={`cp-ico cp-ico-flux ${className}`} style={{ width: '1.25em', height: '1.25em', verticalAlign: '-0.27em', ...style }}
      aria-hidden="true" dangerouslySetInnerHTML={{ __html: FLUX_SVG[name] }} />;
  }
  return <span className={`cp-ico ico-${motionOf(e)} ${className}`} style={style} aria-hidden="true">{e}</span>;
}

// '📤 Outbox' → [<Emo/>, ' Outbox']; anything else unchanged
const LEAD = /^(\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier}|\u200D\p{Extended_Pictographic})*|[↻★+⬇])\s*/u;   // 3.43.5: skin tones and joined emoji too
export function emoLabel(label) {
  if (typeof label !== 'string') return label;
  const m = label.match(LEAD);
  if (!m) return label;
  return <><Emo e={m[1]} /> {label.slice(m[0].length)}</>;
}
